import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  IconButton,
  AppBar,
  Toolbar,
  Drawer,
  Grid,
  Slider,
  Divider,
  InputAdornment,
  CircularProgress,
  MenuItem,
  Chip,
  Checkbox,
  Autocomplete
} from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import AuthContext from "flexbiz-core/components/auth/Context";
import { formatCurrency } from "flexbiz-core/utils";
import { NumericFormat } from 'react-number-format';

// Icons
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';

const steps = ['Chọn BĐS', 'Giao dịch', 'Xác nhận'];

export default function SalesPage() {
  const history = useHistory();
  const { ma_dt } = useParams(); 
  const context = useContext(AuthContext); 
  const [activeStep, setActiveStep] = useState(0);

  // --- STATE BƯỚC 1: TÌM & CHỌN NHIỀU LÔ ---
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlots, setSelectedPlots] = useState([]);
  
  const [filterOpen, setFilterOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    ma_nd: '', so_to: '', so_thua: '', gia_ban: [0, 20000000000], dien_tich: [0, 500],
    min_chieu_ngang: '', max_chieu_ngang: '', min_chieu_dai: '', max_chieu_dai: ''
  });
  const [appliedFilters, setAppliedFilters] = useState(null); 

  // --- STATE BƯỚC 2: THÔNG TIN KHÁCH & GIAO DỊCH ---
  const [customerInfo, setCustomerInfo] = useState({
    ten_nguoi_mua: '', so_cccd: '', dien_thoai: '', dia_chi: '',
    pt_thanh_toan: '' 
  });

  const [customerSearchKw, setCustomerSearchKw] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPayment, setLoadingPayment] = useState(false);

  const [giaBanDeNghi, setGiaBanDeNghi] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (!ma_dt) return;
      setLoadingPayment(true);
      try {
        const response = await context.apis.asyncGetList(
          context.userInfo.token, "dmdt_bds_pttt", 
          { 
            fields: "ma_pttt,ten_pttt,ghi_chu",
            condition: { ma_dt: ma_dt } 
          }
        );
        const data = response?.data || response || [];
        setPaymentMethods(data);

        if (data.length > 0 && !customerInfo.pt_thanh_toan) {
          setCustomerInfo(prev => ({ ...prev, pt_thanh_toan: data[0].ma_pttt }));
        }
      } catch (error) {
        console.error("Lỗi tải phương thức thanh toán:", error);
      } finally {
        setLoadingPayment(false);
      }
    };

    fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ma_dt]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDataFromApi(searchTerm, appliedFilters);
    }, 500);
    return () => clearTimeout(timer); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, appliedFilters, ma_dt]);

  const fetchDataFromApi = async (keyword, filters) => {
    setLoading(true);
    try {
      const conditionArray = [{ ma_dt: ma_dt }, { trang_thai: "5" }];

      if (keyword) {
        const generateSearchVariations = (str) => {
          const base = str.replace(/[\s-]/g, ''); 
          const variations = [str, base]; 
          if (base.length > 1 && base.length <= 10) {
            for (let i = 1; i < base.length; i++) {
              variations.push(`${base.slice(0, i)}-${base.slice(i)}`); 
              variations.push(`${base.slice(0, i)} ${base.slice(i)}`); 
            }
          }
          return [...new Set(variations)]; 
        };

        const searchTerms = generateSearchVariations(keyword);
        const maNdConditions = searchTerms.map(term => ({
          ma_nd: { $regex: term, $options: 'i' }
        }));

        conditionArray.push({
          $or: [
            ...maNdConditions,
            { so_to: { $regex: keyword, $options: 'i' } },
            { so_thua: { $regex: keyword, $options: 'i' } },
            { ten_nd: { $regex: keyword, $options: 'i' } } 
          ]
        });
      }

      if (filters) {
        if (filters.ma_nd) {
          const filterAlpha = filters.ma_nd.replace(/[^a-zA-Z0-9]/g, '');
          const filterRegex = filterAlpha ? filterAlpha.split('').join('[^a-zA-Z0-9]*') : filters.ma_nd;
          conditionArray.push({ ma_nd: { $regex: filterRegex, $options: 'i' } });
        }
        if (filters.so_to) conditionArray.push({ so_to: filters.so_to }); 
        if (filters.so_thua) conditionArray.push({ so_thua: filters.so_thua });
        conditionArray.push({ gia_ban: { $gte: filters.gia_ban[0], $lte: filters.gia_ban[1] } });
        conditionArray.push({ dien_tich: { $gte: filters.dien_tich[0], $lte: filters.dien_tich[1] } });
        
        if (filters.min_chieu_ngang) conditionArray.push({ chieu_ngang: { $gte: Number(filters.min_chieu_ngang) } });
        if (filters.max_chieu_ngang) conditionArray.push({ chieu_ngang: { $lte: Number(filters.max_chieu_ngang) } });
        if (filters.min_chieu_dai) conditionArray.push({ chieu_dai: { $gte: Number(filters.min_chieu_dai) } });
        if (filters.max_chieu_dai) conditionArray.push({ chieu_dai: { $lte: Number(filters.max_chieu_dai) } });
      }

      const response = await context.apis.asyncGetList(
        context.userInfo.token, "nhadat", 
        { 
          fields: "ma_dt,ma_nd,ten_nd,so_to,so_thua,dien_tich,chieu_ngang,chieu_dai,gia_ban,ty_le_hh,chiet_khau_khuyen_mai", 
          limit: 20, 
          condition: { $and: conditionArray } 
        }
      );

      const data = response?.data || response || [];
      setSuggestions(data);
    } catch (error) {
      console.error("Lỗi khi tải danh sách sản phẩm:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearchKw && customerSearchKw.length >= 2) {
        fetchCustomers(customerSearchKw);
      } else {
        setCustomerOptions([]);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearchKw]);

  const fetchCustomers = async (kw) => {
    setLoadingCustomers(true);
    try {
      const response = await context.apis.asyncGetList(
        context.userInfo.token, "customer", 
        {
          fields: "ma_kh,ten_kh,dien_thoai,cccd,dia_chi",
          limit: 10,
          condition: {
            $or: [
              { ten_kh: { $regex: kw, $options: 'i' } },
              { dien_thoai: { $regex: kw, $options: 'i' } },
              { cccd: { $regex: kw, $options: 'i' } }
            ]
          }
        }
      );
      setCustomerOptions(response?.data || response || []);
    } catch (error) {
      console.error("Lỗi khi tìm khách hàng:", error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleTogglePlot = (plot) => {
    const isSelected = selectedPlots.some(p => p._id === plot._id);
    if (isSelected) {
      setSelectedPlots(prev => prev.filter(p => p._id !== plot._id));
      setGiaBanDeNghi(prev => {
        const newState = { ...prev };
        delete newState[plot._id];
        return newState;
      });
    } else {
      setSelectedPlots(prev => [...prev, plot]);
      setGiaBanDeNghi(prev => ({ ...prev, [plot._id]: plot.gia_ban.toString() }));
    }
  };

  const calculatePlotData = (plot) => {
    const deNghi = Number(giaBanDeNghi[plot._id] || plot.gia_ban);
    
    const ckmArray = plot.chiet_khau_khuyen_mai || [];
    const tong_chiet_khuyen_mai = ckmArray
      .filter(c => !c.pt_thanh_toan || c.pt_thanh_toan === customerInfo.pt_thanh_toan)
      .map(c => Number(c.gia_tri || 0))
      .reduce((a, b) => a + b, 0);
      
    const gia_ban_thuc = deNghi - tong_chiet_khuyen_mai;
    const tyLe = Number(plot.ty_le_hh || 0);
    const hoa_hong = (gia_ban_thuc * tyLe) / 100;

    return { deNghi, tong_chiet_khuyen_mai, gia_ban_thuc, hoa_hong, ty_le_hh: tyLe };
  };

  const handleNext = () => {
    if (activeStep === 0 && selectedPlots.length === 0) {
      context.alert("Vui lòng chọn ít nhất một sản phẩm để tiếp tục!");
      return;
    }
    if (activeStep === 1) {
      if (!customerInfo.pt_thanh_toan) {
        context.alert("Vui lòng chọn phương thức thanh toán!");
        return;
      }
      for (let plot of selectedPlots) {
        const price = Number(giaBanDeNghi[plot._id]);
        if (!price || price <= 0) {
          context.alert(`Vui lòng nhập giá bán đề nghị hợp lệ cho lô ${plot.ma_nd}!`);
          return;
        }
      }
    }

    if (activeStep === steps.length - 1) {
      handleSubmitOrder();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      history.goBack();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmitOrder = async () => {
    setIsSubmitting(true);
    try {
      const payloadArray = selectedPlots.map(plot => {
        const math = calculatePlotData(plot);
        return {
          id_app: context.userInfo?.id_app || "", 
          ma_dt: plot.ma_dt || ma_dt,                           
          ma_nd: plot.ma_nd,              
          ten_nguoi_mua: customerInfo.ten_nguoi_mua,
          dien_thoai: customerInfo.dien_thoai,    
          so_cccd: customerInfo.so_cccd,
          dia_chi: customerInfo.dia_chi,
          pt_thanh_toan: customerInfo.pt_thanh_toan,
          
          gia_ban: plot.gia_ban,
          gia_ban_de_nghi: math.deNghi,
          tong_chiet_khau_khuyen_mai: math.tong_chiet_khuyen_mai,
          gia_ban_thuc: math.gia_ban_thuc,     
          
          ty_le_hh: math.ty_le_hh,
          hoa_hong: math.hoa_hong,

          trang_thai: "0",                  
        };
      });

      const response = await context.apis.asyncPostList(
        context.userInfo.token, "nhadat_moigioi_ban", payloadArray
      );

      const apiError = response?.error || response?.data?.error;
      if (apiError) {
        context.alert("Lỗi tạo đơn: " + apiError);
        return; 
      }
      history.replace('/ctv-sales-history');
      setTimeout(()=>{
        context.alert("Tạo đơn hàng thành công!");
      },10)

    } catch (error) {
      console.error("Exception khi submit order:", error);
      context.alert("Lỗi hệ thống: " + (error.response?.data?.error || error.message || "Lỗi kết nối máy chủ!"));
    } finally {
      setIsSubmitting(false); 
    }
  };

  // ================= CÁC BƯỚC RENDER =================
  const renderStep1 = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth size="small" placeholder="Nhập mã hoặc tên lô..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
            endAdornment: loading && <CircularProgress size={20} />
          }}
          sx={{ bgcolor: 'white', borderRadius: 1 }}
        />
        <Button 
          variant="outlined" 
          onClick={(e) => { e.stopPropagation(); setFilterOpen(true); }} 
          sx={{ minWidth: 48, p: 0, bgcolor: 'white', zIndex: 10 }}
        >
          <TuneIcon />
        </Button>
      </Box>

      {appliedFilters && (
        <Typography variant="caption" color="primary" sx={{ mb: 2, display: 'block', fontStyle: 'italic' }}>
          *Đang áp dụng bộ lọc nâng cao
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">Danh sách ({suggestions.length})</Typography>
        {selectedPlots.length > 0 && (
          <Typography variant="subtitle2" color="primary" fontWeight="bold">Đã chọn {selectedPlots.length} lô</Typography>
        )}
      </Box>

      <Box>
        {suggestions.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
            Không tìm thấy sản phẩm nào phù hợp.
          </Typography>
        )}
        
        {suggestions.map((plot) => {
          const isSelected = selectedPlots.some(p => p._id === plot._id);
          
          return (
            <Card key={plot._id || plot.id} sx={{ mb: 1.5, borderRadius: 2, border: isSelected ? '2px solid #1976d2' : '1px solid transparent', bgcolor: isSelected ? '#f0f7ff' : 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <CardActionArea onClick={() => handleTogglePlot(plot)} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Checkbox checked={isSelected} color="primary" sx={{ p: 0, pr: 1.5, mt: -0.5 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight="bold" color={isSelected ? 'primary' : 'text.primary'}>
                        {plot.ma_nd} {plot.ten_nd ? `- ${plot.ten_nd}` : ''}
                      </Typography>
                      {plot.ty_le_hh > 0 && <Chip size="small" label={`HH: ${plot.ty_le_hh}%`} sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 'bold', height: 20 }} />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Tờ: {plot.so_to || '---'} | Thửa: {plot.so_thua || '---'} | DT: {plot.dien_tich} m²
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 0.5 }}>
                      <AspectRatioIcon sx={{ fontSize: 16, mr: 0.5, color: '#8e8e93' }} />
                      <Typography variant="body2" fontWeight="500">
                        Kích thước: {plot.chieu_ngang || '?'}m x {plot.chieu_dai || '?'}m
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{ color: '#d32f2f', mt: 0.5 }}>
                      {formatCurrency(plot.gia_ban)}
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>
  );

  const renderStep2 = () => {
    const selectedMethodInfo = paymentMethods.find(m => m.ma_pttt === customerInfo.pt_thanh_toan);

    return (
      <Box>
        <Card sx={{ borderRadius: 2, p: 2, mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PersonIcon fontSize="small" sx={{ mr: 1 }} /> Thông tin khách hàng & Thanh toán
          </Typography>
          <Grid container spacing={2}>
            
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                freeSolo
                options={customerOptions}
                filterOptions={(x) => x} 
                getOptionLabel={(option) => typeof option === 'string' ? option : `${option.ten_kh} - ${option.dien_thoai||""}`}
                inputValue={customerSearchKw}
                onInputChange={(event, newInputValue) => {
                  setCustomerSearchKw(newInputValue);
                  setCustomerInfo(prev => ({ ...prev, ten_nguoi_mua: newInputValue }));
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === 'object') {
                    setCustomerInfo(prev => ({
                      ...prev,
                      ten_nguoi_mua: newValue.ten_kh || '',
                      dien_thoai: newValue.dien_thoai || prev.dien_thoai,
                      so_cccd: newValue.cccd || newValue.so_cccd || prev.so_cccd, 
                      dia_chi: newValue.dia_chi || prev.dia_chi
                    }));
                    setCustomerSearchKw(newValue.ten_kh || '');
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.ma_kh || option._id}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold" color="primary">{option.ten_kh}</Typography>
                      <Typography variant="caption" color="text.secondary">SĐT: {option.dien_thoai} {option.cccd ? `| CCCD: ${option.cccd}` : ''}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Họ và tên người mua (Hoặc SĐT)"
                    size="small"
                    placeholder="Gõ để tìm khách cũ hoặc nhập mới..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Số điện thoại" size="small" type="tel" value={customerInfo.dien_thoai} onChange={(e) => setCustomerInfo({...customerInfo, dien_thoai: e.target.value})} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Số CCCD" size="small" value={customerInfo.so_cccd} onChange={(e) => setCustomerInfo({...customerInfo, so_cccd: e.target.value})} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Địa chỉ thường trú" size="small" multiline rows={2} value={customerInfo.dia_chi} onChange={(e) => setCustomerInfo({...customerInfo, dia_chi: e.target.value})} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField 
                select 
                fullWidth 
                label="Phương thức thanh toán *" 
                size="small" 
                value={customerInfo.pt_thanh_toan} 
                onChange={(e) => setCustomerInfo({...customerInfo, pt_thanh_toan: e.target.value})}
                disabled={loadingPayment}
                InputProps={{
                  startAdornment: loadingPayment ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null
                }}
              >
                {paymentMethods.length === 0 && !loadingPayment && (
                  <MenuItem value="" disabled>Không có PTTT nào cấu hình</MenuItem>
                )}
                {paymentMethods.map(method => (
                  <MenuItem key={method.ma_pttt || method._id} value={method.ma_pttt}>
                    {method.ten_pttt}
                  </MenuItem>
                ))}
              </TextField>

              {selectedMethodInfo && selectedMethodInfo.ghi_chu && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px dashed #90caf9' }}>
                  <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic', display: 'block', lineHeight: 1.4 }}>
                    <span style={{ fontWeight: 'bold' }}>* Lưu ý: </span> {selectedMethodInfo.ghi_chu}
                  </Typography>
                </Box>
              )}
            </Grid>
            
          </Grid>
        </Card>

        <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 1 }}>
          <RequestQuoteIcon fontSize="small" sx={{ mr: 1 }} /> Đề xuất giá cho {selectedPlots.length} sản phẩm
        </Typography>

        {selectedPlots.map((plot) => (
          <Card key={plot._id} sx={{ borderRadius: 2, p: 2, mb: 1.5, borderLeft: '4px solid #1976d2' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight="bold">{plot.ma_nd}</Typography>
              <Typography variant="body2" color="text.secondary">Niêm yết: {formatCurrency(plot.gia_ban)}</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2, bgcolor: '#f2f2f7', p: 1, borderRadius: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Chiều ngang</Typography>
                <Typography variant="body2" fontWeight="bold" color="#1c1c1e">{plot.chieu_ngang || '---'} m</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Chiều dài</Typography>
                <Typography variant="body2" fontWeight="bold" color="#1c1c1e">{plot.chieu_dai || '---'} m</Typography>
              </Box>
            </Box>

            <NumericFormat
              customInput={TextField}
              fullWidth
              readOnly
              label="Giá bán đề nghị (VNĐ) *"
              size="small"
              value={giaBanDeNghi[plot._id] || ''}
              thousandSeparator="."
              decimalSeparator=","
              allowNegative={false}
              InputProps={{ 
                readOnly: true, // Thêm dòng này
              }}
              onValueChange={(values) => {
                setGiaBanDeNghi(prev => ({ ...prev, [plot._id]: values.value }));
              }}
            />
          </Card>
        ))}
      </Box>
    );
  };

  const renderStep3 = () => {
    let totalCommission = 0;
    let totalRealPrice = 0;

    selectedPlots.forEach(plot => {
      const math = calculatePlotData(plot);
      totalCommission += math.hoa_hong;
      totalRealPrice += math.gia_ban_thuc;
    });

    const selectedMethodInfo = paymentMethods.find(m => m.ma_pttt === customerInfo.pt_thanh_toan);
    const paymentMethodName = selectedMethodInfo ? selectedMethodInfo.ten_pttt : customerInfo.pt_thanh_toan;

    return (
      <Box>
        <Card sx={{ mb: 2, borderRadius: 2, p: 2, background: 'linear-gradient(135deg, #ff9800 0%, #ed6c02 100%)', color: 'white', boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)' }}>
          <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <AccountBalanceWalletIcon fontSize="small" sx={{ mr: 1 }} /> Tổng Hoa Hồng Dự Kiến
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {formatCurrency(totalCommission)}
          </Typography>
          
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 1.5 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>Tổng giá trị HĐ (Đã trừ CK):</Typography>
            <Typography variant="subtitle2" fontWeight="bold">{formatCurrency(totalRealPrice)}</Typography>
          </Box>
        </Card>

        <Card sx={{ borderRadius: 2, p: 2, mb: 2, borderTop: '4px solid #4caf50' }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PersonIcon fontSize="small" sx={{ mr: 1 }} /> Khách hàng: {customerInfo.ten_nguoi_mua}
          </Typography>
          <Typography variant="body2">
            SĐT: {customerInfo.dien_thoai} <br/>
            Thanh toán: <b>{paymentMethodName}</b>
          </Typography>
        </Card>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
          Chi tiết {selectedPlots.length} sản phẩm:
        </Typography>

        {selectedPlots.map((plot) => {
          const math = calculatePlotData(plot);

          return (
            <Card key={plot._id} sx={{ mb: 2, borderRadius: 2, p: 2, borderTop: '4px solid #1976d2' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary">{plot.ma_nd}</Typography>
                {math.ty_le_hh > 0 && <Chip size="small" label={`HH: ${math.ty_le_hh}%`} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }} />}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 1.5, bgcolor: '#f2f2f7', p: 1, borderRadius: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Chiều ngang</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#1c1c1e">{plot.chieu_ngang || '---'} m</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Chiều dài</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#1c1c1e">{plot.chieu_dai || '---'} m</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />
              
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Giá đề nghị:</Typography>
                    <Typography variant="body2" fontWeight="500">{formatCurrency(math.deNghi)}</Typography>
                  </Box>
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#e65100' }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocalOfferIcon sx={{ fontSize: 14, mr: 0.5 }} /> Tổng CK/KM:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">- {formatCurrency(math.tong_chiet_khuyen_mai)}</Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Giá bán thực (Ký HĐ):</Typography>
                    <Typography variant="subtitle2" fontWeight="bold" color="#d32f2f">
                      {formatCurrency(math.gia_ban_thuc)}
                    </Typography>
                  </Box>
                </Grid>
                
                {math.ty_le_hh > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#fff3e0', p: 1, borderRadius: 1, mt: 0.5 }}>
                      <Typography variant="caption" color="#e65100" fontWeight="bold">Hoa hồng trực tiếp:</Typography>
                      <Typography variant="subtitle2" color="#e65100" fontWeight="bold">
                        {formatCurrency(math.hoa_hong)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    // 1. Áp dụng 100dvh cho Box bọc ngoài cùng
    <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', backgroundColor: '#f4f6f8', overflow: 'hidden' }}>
      <AppBar position="static" elevation={0} sx={{ flexShrink: 0, bgcolor: 'white', color: 'text.primary', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar sx={{ px: 1 }}>
          <IconButton onClick={() => history.goBack()} edge="start">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1, textAlign: 'center', pr: 4 }}>
            Tạo đơn bán hàng
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ flexShrink: 0, width: '100%', bgcolor: 'white', py: 2, px: 1, borderBottom: '1px solid #eee' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Box trung tâm sẽ tự động co giãn và cho phép scroll khi cần thiết */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
      </Box>

      {/* 2. Áp dụng safe-area-inset-bottom cho Footer */}
      <Box sx={{ 
        flexShrink: 0, 
        px: 2, 
        pt: 2, 
        pb: 'max(16px, env(safe-area-inset-bottom))',
        bgcolor: 'white', 
        borderTop: '1px solid #e0e0e0', 
        display: 'flex', 
        justifyContent: 'space-between' 
      }}>
        <Button disabled={isSubmitting} onClick={handleBack} variant="outlined" sx={{ width: '48%' }}>
          Quay lại
        </Button>
        <Button disabled={isSubmitting} onClick={handleNext} variant="contained" color="primary" sx={{ width: '48%' }} startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}>
          {isSubmitting ? 'Đang xử lý...' : activeStep === steps.length - 1 ? 'Chốt đơn' : 'Tiếp tục'}
        </Button>
      </Box>

      {/* 3. Đã xoá sx={{ zIndex: 9999 }} khỏi Drawer */}
      <Drawer
        anchor="bottom"
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, pb: 4, px: 2, pt: 1, maxHeight: '85vh' } }}
      >
        <Box sx={{ display: 'flex', justifyItems: 'center', justifyContent: 'center', mb: 1 }}>
          <Box sx={{ width: 40, height: 4, bgcolor: '#e0e0e0', borderRadius: 2 }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">Lọc nâng cao</Typography>
          <IconButton onClick={() => setFilterOpen(false)} size="small"><CloseIcon /></IconButton>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <TextField fullWidth label="Mã lô" size="small" value={advancedFilters.ma_nd} onChange={(e) => setAdvancedFilters({...advancedFilters, ma_nd: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField fullWidth label="Số tờ" size="small" value={advancedFilters.so_to} onChange={(e) => setAdvancedFilters({...advancedFilters, so_to: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>Chiều ngang (m)</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField fullWidth size="small" type="number" placeholder="Từ..." value={advancedFilters.min_chieu_ngang} onChange={(e) => setAdvancedFilters({...advancedFilters, min_chieu_ngang: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField fullWidth size="small" type="number" placeholder="Đến..." value={advancedFilters.max_chieu_ngang} onChange={(e) => setAdvancedFilters({...advancedFilters, max_chieu_ngang: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Chiều dài (m)</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField fullWidth size="small" type="number" placeholder="Từ..." value={advancedFilters.min_chieu_dai} onChange={(e) => setAdvancedFilters({...advancedFilters, min_chieu_dai: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField fullWidth size="small" type="number" placeholder="Đến..." value={advancedFilters.max_chieu_dai} onChange={(e) => setAdvancedFilters({...advancedFilters, max_chieu_dai: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Khoảng giá: {formatCurrency(advancedFilters.gia_ban[0])} - {formatCurrency(advancedFilters.gia_ban[1])}
            </Typography>
            <Box sx={{ px: 2 }}>
              <Slider value={advancedFilters.gia_ban} onChange={(e, newValue) => setAdvancedFilters({...advancedFilters, gia_ban: newValue})} min={0} max={20000000000} step={500000000} valueLabelDisplay="off" />
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Diện tích: {advancedFilters.dien_tich[0]} m² - {advancedFilters.dien_tich[1]} m²
            </Typography>
            <Box sx={{ px: 2 }}>
              <Slider value={advancedFilters.dien_tich} onChange={(e, newValue) => setAdvancedFilters({...advancedFilters, dien_tich: newValue})} min={0} max={500} step={10} valueLabelDisplay="off" />
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button variant="outlined" fullWidth onClick={() => {
                setAdvancedFilters({ ma_nd: '', so_to: '', so_thua: '', gia_ban: [0, 20000000000], dien_tich: [0, 500], min_chieu_ngang: '', max_chieu_ngang: '', min_chieu_dai: '', max_chieu_dai: '' });
                setAppliedFilters(null);
                setFilterOpen(false);
              }}>
                Xóa lọc
              </Button>
              <Button variant="contained" fullWidth onClick={() => {
                setAppliedFilters(advancedFilters);
                setFilterOpen(false);
              }}>
                Áp dụng
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Drawer>
    </Box>
  );
}